import {
    NinchatProperties, NinchatState,
} from 'api/types'

import React, {
    useContext, useEffect, useRef,
} from 'react'

import {
    embedQueue, embedQueueAdditionalContent,
} from './styles'

import {
    useAppDispatch, useAppSelector,
} from 'hooks'

import {EmbedContext} from 'components/EmbedContext'
import {EmbedEvent} from 'embed/types/events'
import {EmbedHero} from './components/EmbedHero/EmbedHero'
import {NinchatAudienceEnqueuedEvent} from 'api/types/events'
import {NinchatAudienceMetadata} from 'api/types/properties'

import {QueueProps} from 'components/Queue/types'
import ReactMarkdown from 'react-markdown'
import {RouteParamsList} from 'navigation/types'

import {config} from 'embed/listeners'
import {markdownProps} from './helpers/markdown'
import {requestAudience} from 'api/action/requestAudience'
import {styled} from 'stitches/config'
import {useSendToParent} from 'embed/utils/event'
import {useTranslation} from 'react-i18next'


const StyledQueue = styled('div', embedQueue)
const StyledQueueAdditionalContent = styled('div', embedQueueAdditionalContent)
interface EmbedQueueProps {
    fileIds: Array<NinchatProperties.FileID>
    metadata: RouteParamsList['EmbedQueue']['metadata'] & Partial<NinchatAudienceMetadata>
    queueId: string
    resetEmbed: (clearData?: boolean) => void
}

const parseQueueProps = (ninchat: NinchatState, queueId: string): QueueProps => {
    const queueAttrsMap = ninchat?.user_queues
    return queueAttrsMap?.[queueId] as QueueProps
}

export const EmbedQueue = (props: EmbedQueueProps): React.ReactElement => {
    const {
        queueId, metadata, resetEmbed, fileIds,
    } = props
    const embed = useContext(EmbedContext)
    const sendToParent = useSendToParent()
    const {t} = useTranslation()
    const [selectedAudience, setSelectedAudience] = React.useState<string | null>(null)

    const dispatch = useAppDispatch()

    // undefined until the audience is first enqueued; used to tell first enqueue from later position changes
    const previousQueuePosition = useRef<number | undefined>(undefined)

    const queueProps = useAppSelector(({ninchat}: { ninchat: NinchatState }): QueueProps => parseQueueProps(ninchat, queueId))

    const initMetadata = embed?.audienceMetadata || {}

    useEffect(() => {
        if (queueId && queueProps === undefined) {
            dispatch(requestAudience({
                audience_metadata: {
                    ...initMetadata,
                    post_answers: {
                        ...initMetadata?.post_answers,
                        ...metadata.post_answers,
                    },
                    pre_answers: {
                        ...initMetadata?.pre_answers,
                        ...metadata.pre_answers,
                    },
                },
                file_ids: fileIds,
                queue_id: queueId,
            }))
                .unwrap()
                .then((data) => {
                    const {
                        audience_id, queue_position,
                    } = data as NinchatAudienceEnqueuedEvent
                    setSelectedAudience(audience_id)
                    // send event to parent that audience has been enqueued
                    sendToParent({
                        audience_id,
                        event: EmbedEvent.AudienceEnqueued,
                        position: queue_position,
                        queue_id: queueId,
                        targetOrigin: config.get('targetOrigin') || '',
                    })
                })
                .catch((error) => {
                    let title = t('Error requesting audience')

                    if (error.error_type === 'permission_denied') {
                        title = t('Session expired')
                    }

                    embed?.setEmbedState((prevState) => ({
                        ...prevState,
                        error: {
                            message: t('We encountered an error. Please close and restart chat to continue.'),
                            title,
                        },
                    }))
                })
        }
    }, [])

    useEffect(() => {
        if (queueProps?.queue_position !== undefined) {
            // send queue position to parent
            sendToParent({
                event: EmbedEvent.EmbedAudienceQueue,
                queuePosition: queueProps?.queue_position,
                targetOrigin: config.get('targetOrigin') || '',
            })

            if (previousQueuePosition.current === undefined) {
                sendToParent({
                    event: EmbedEvent.EmbedSound,
                    sound: 'queued',
                    targetOrigin: config.get('targetOrigin') || '',
                })
            } else if (previousQueuePosition.current !== queueProps.queue_position) {
                sendToParent({
                    event: EmbedEvent.EmbedSound,
                    sound: 'queuePositionChanged',
                    targetOrigin: config.get('targetOrigin') || '',
                })
            }

            previousQueuePosition.current = queueProps.queue_position

            if (queueProps?.queue_position === 1) {
                sendToParent({
                    audience_id: selectedAudience || '',
                    event: EmbedEvent.AudienceEnqueuedNext,
                    queue_id: queueId,
                    targetOrigin: config.get('targetOrigin') || '',
                })
            }
        }
    }, [queueProps?.queue_position, selectedAudience])

    return (
        <StyledQueue>
            <EmbedHero members={[]} queueProps={queueProps} resetEmbed={resetEmbed} type='queue' />
            <StyledQueueAdditionalContent>
                <ReactMarkdown {...markdownProps}>
                    {embed?.inQueueText || ''}
                </ReactMarkdown>
            </StyledQueueAdditionalContent>
        </StyledQueue>
    )
}
